import React, { Dispatch } from 'react';
import { Autocomplete, Button, Stack, TextField } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { styles } from '../../styles';
import FormRadioButtonGroup from '../../../../Components/CustomUIElements/FormRadioButtonGroup';
import { sharedClasses } from '../../../../Components/CustomUIElements/sharedClasses';
import { useQueryClient } from '@tanstack/react-query';
import { IIntelliHrData } from '../../../types';
import { IntelliHRState, IntelliHRAction } from './intelliHRReducer';
import { FormTextField } from '../../../../Components/CustomUIElements/FormTextField';
import CurrencyInput from './components/CurrencyInput';
import AdditionTable from './components/AdditionTable';

export default function RenumerationScheduleStep({
  state,
  dispatch,
  fieldRefs
}: {
  state: IntelliHRState;
  dispatch: Dispatch<IntelliHRAction>;
  fieldRefs: {
    salary: React.RefObject<HTMLInputElement>;
    payCycle: React.RefObject<HTMLInputElement>;
    hoursPerCycle: React.RefObject<HTMLInputElement>;
  };
}) {
  const queryClient = useQueryClient();
  const intelliHrData = queryClient.getQueryData<IIntelliHrData>(['intelliHr data']);
  const queryState = queryClient.getQueryState(['intelliHr data']);
  const loadingIntelliHrData = queryState?.fetchStatus === 'fetching';
  const { additionToBase, additionToTotal, breakdowns, currency, salary } = state;

  const AdditionButton = ({ onClick }: { onClick: () => void }) => (
    <Button
      sx={{ ...sharedClasses.genericButtonSecondary, width: 'fit-content', padding: '6px 16px' }}
      startIcon={<AddIcon />}
      onClick={onClick}
    >
      Add Addition
    </Button>
  );

  return (
    <>
      <Stack sx={styles.cognologySectionTitle}>Renumeration Schedule</Stack>
      <FormRadioButtonGroup
        options={[
          { label: 'Annual salary', value: 'Annual Salary' },
          { label: 'Hourly rate', value: 'Hourly Rate' }
        ]}
        onChange={(value) =>
          dispatch({ type: 'SET_RENUMERATION_SCHEDULE_TYPE', payload: value.toString() })
        }
        directionRow
        color={'#939393'}
        defaultValue={state.renumerationScheduleType}
        groupLabel="Renumeration schedule type"
        required
      />
      <CurrencyInput
        value={salary}
        currency={currency}
        onChange={(value) => dispatch({ type: 'SET_SALARY', payload: value })}
        onCurrencyChange={(value) => dispatch({ type: 'SET_CURRENCY', payload: value })}
        label="Base annual salary"
        required
        error={state.errors.salary}
        innerRef={fieldRefs.salary}
      />
      <Stack sx={styles.modalDoubleColumn}>
        <Autocomplete
          disablePortal
          options={intelliHrData?.pay_cycle || []}
          value={state.payCycle}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '96%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pay cycle"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              helperText={state.errors.payCycle}
              ref={fieldRefs.payCycle}
              sx={styles.autoCompleteTextfield}
              required
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_PAY_CYCLE', payload: value })}
        />
        <FormTextField
          label="Hours per cycle"
          value={state.hoursPerCycle}
          required
          fullWidth
          onChange={(e) => dispatch({ type: 'SET_HOURS_PER_CYCLE', payload: e.target.value })}
          error={state.errors.hoursPerCycle}
          innerRef={fieldRefs.hoursPerCycle}
        />
      </Stack>

      <Stack sx={{ rowGap: 1.5 }}>
        <Stack sx={styles.cognologySectionTitle}>Addition to Base</Stack>
        <AdditionTable
          items={additionToBase}
          onUpdate={(items) => dispatch({ type: 'SET_ADDITION_TO_BASE', payload: items })}
        />
        <AdditionButton
          onClick={() =>
            dispatch({
              type: 'SET_ADDITION_TO_BASE',
              payload: [...additionToBase, { name: '', type: '', value: { value: '', currency } }]
            })
          }
        />
      </Stack>

      <Stack sx={{ rowGap: 1.5 }}>
        <Stack sx={styles.cognologySectionTitle}>Addition to Total</Stack>
        <AdditionTable
          items={additionToTotal}
          onUpdate={(items) => dispatch({ type: 'SET_ADDITION_TO_TOTAL', payload: items })}
          isReadOnly
        />
        <AdditionButton
          onClick={() =>
            dispatch({
              type: 'SET_ADDITION_TO_TOTAL',
              payload: [
                ...additionToTotal,
                {
                  name: 'Superannuation',
                  type: 'percentage',
                  value: { value: '', currency }
                }
              ]
            })
          }
        />
      </Stack>

      <Stack sx={{ rowGap: 1.5 }}>
        <Stack sx={styles.cognologySectionTitle}>Breakdowns</Stack>
        <AdditionTable
          items={breakdowns}
          onUpdate={(items) => dispatch({ type: 'SET_BREAKDOWNS', payload: items })}
        />
        <AdditionButton
          onClick={() =>
            dispatch({
              type: 'SET_BREAKDOWNS',
              payload: [...breakdowns, { name: '', type: '', value: { value: '', currency } }]
            })
          }
        />
      </Stack>
    </>
  );
}
